import { getActiveTenantKey, getTenantScopedKey } from '../offline/tenant'

import { deleteKey, loadJSON, readItem, saveJSON, writeItem } from './storage'

const hasWindow = (): boolean => typeof window !== 'undefined'

const normalizeBaseKey = (key: string): string => {
  const raw = (key ?? '').trim()
  if (!raw) return raw
  const parts = raw.split('::')
  return parts[0] ?? raw
}

const getScopedKey = (baseKey: string, tenantKey = getActiveTenantKey()): string => (
  getTenantScopedKey(normalizeBaseKey(baseKey), tenantKey)
)

export const readScopedString = (baseKey: string, tenantKey = getActiveTenantKey()): string | null => {
  const scopedKey = getScopedKey(baseKey, tenantKey)
  const scoped = readItem(scopedKey)
  if (scoped !== null) return scoped

  if (tenantKey === 'default') {
    return readItem(normalizeBaseKey(baseKey))
  }

  return null
}

export const writeScopedString = (baseKey: string, value: string, tenantKey = getActiveTenantKey()): void => {
  const scopedKey = getScopedKey(baseKey, tenantKey)
  if (!hasWindow()) return
  try {
    // Routed like every other write (findings #75): a dataset key goes to the
    // IndexedDB-backed store, so reads (`readScopedString`) see it.
    writeItem(scopedKey, value)

    if (tenantKey === 'default') {
      deleteKey(normalizeBaseKey(baseKey))
    }
  } catch (error) {
    console.warn(`[waterStorage] Failed to write key "${scopedKey}"`, error)
  }
}

export const deleteScopedKey = (baseKey: string, tenantKey = getActiveTenantKey()): void => {
  const normalized = normalizeBaseKey(baseKey)
  deleteKey(getTenantScopedKey(normalized, tenantKey))

  if (tenantKey === 'default') {
    deleteKey(normalized)
  }
}

export const readWaterJSON = <T>(baseKey: string, fallback: T, tenantKey = getActiveTenantKey()): T => {
  const scopedKey = getScopedKey(baseKey, tenantKey)
  if (readItem(scopedKey) !== null) {
    return loadJSON<T>(scopedKey, fallback)
  }

  if (tenantKey === 'default') {
    const normalized = normalizeBaseKey(baseKey)
    if (readItem(normalized) !== null) {
      const value = loadJSON<T>(normalized, fallback)
      // Backfill into scoped storage for default tenant.
      try {
        saveJSON(scopedKey, value)
      } catch {
        // ignore
      }
      return value
    }
  }

  return fallback
}

export const writeWaterJSON = <T>(baseKey: string, value: T, tenantKey = getActiveTenantKey()): void => {
  const scopedKey = getScopedKey(baseKey, tenantKey)
  saveJSON(scopedKey, value)

  if (tenantKey === 'default') {
    deleteKey(normalizeBaseKey(baseKey))
  }
}

export const deleteWaterKey = (baseKey: string, tenantKey = getActiveTenantKey()): void => {
  deleteScopedKey(baseKey, tenantKey)
}

export const toTenantWaterKey = (key: string, tenantKey = getActiveTenantKey()): string => (
  getScopedKey(key, tenantKey)
)
